"use client";

import { useEffect, useRef, useState } from "react";
import { Wordmark } from "@/components/wordmark";
import { Mark, type MarkName } from "@/components/mark";
import { greeting } from "@/lib/seen";
import styles from "./hello.module.css";

/* The hello a returning local gets, once a San Diego day.
   It sits over the app while the app is already loading behind it, so it costs nothing
   but a second of attention, and it leaves on its own. */

type Moment = { greet: string; nudge: string; mark: MarkName };

// Only things that are true from a clock. Nothing here pretends to know the weather.
function moment(hour: number, weekday: number): Moment {
  if (hour < 11) return { greet: "Morning", nudge: "Coffee before the day starts?", mark: "coffee" };
  if (hour < 15) return { greet: "Afternoon", nudge: "Lunch somewhere you haven't been?", mark: "food" };
  if (hour < 18) {
    return weekday === 5 || weekday === 6
      ? { greet: "Evening", nudge: "It's the weekend. The block is open.", mark: "sun" }
      : { greet: "Evening", nudge: "Something new before dinner?", mark: "sun" };
  }
  if (hour < 22) return { greet: "Evening", nudge: "Plenty still open near you.", mark: "drinks" };
  return { greet: "Late one", nudge: "A few places are still going.", mark: "wave" };
}

export function Hello({ name }: { name: string | null }) {
  const [show, setShow] = useState(false);
  const [out, setOut] = useState(false);
  const said = useRef<Moment | null>(null);

  useEffect(() => {
    if (!name || !greeting.due()) return;
    const now = new Date();
    const sd = new Date(now.toLocaleString("en-US", { timeZone: "America/Los_Angeles" }));
    said.current = moment(sd.getHours(), sd.getDay());
    greeting.done();
    setShow(true);

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const stay = reduce ? 1200 : 2600;
    const leave = setTimeout(() => setOut(true), stay);
    const gone = setTimeout(() => setShow(false), stay + 520);
    return () => { clearTimeout(leave); clearTimeout(gone); };
  }, [name]);

  if (!show || !said.current) return null;
  const { greet, nudge, mark } = said.current;

  return (
    <div
      className={`${styles.hello} ${out ? styles.out : ""}`}
      role="status"
      aria-live="polite"
      onClick={() => setOut(true)}
    >
      <div className={styles.grain} aria-hidden="true" />
      <div className={styles.inner}>
        <span className={styles.mark} aria-hidden="true">
          <Mark name={mark} size={72} color="currentColor" decorative />
        </span>
        <p className={styles.greet}>{greet},</p>
        <h2 className={styles.name}>{name}</h2>
        <p className={styles.nudge}>{nudge}</p>
      </div>
      <div className={styles.foot}>
        <Wordmark />
        <p className={styles.city}>San Diego</p>
      </div>
    </div>
  );
}
